import React, { useCallback, useEffect } from "react";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import useAsyncOptionField from "components/Input/AsyncAutocomplete/useAsyncOptionField";
import { queryAgentOptions } from "../model";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import { Stack, Typography } from "@mui/material";
import { formBuilderParams } from "components/FormBuilder/FormBuilder";

const COMPONENT_LOCALE_KEY = "page.merchants.addMerchant.";

const buildAgentOption = (agent) => {
  const email = agent?.email || "";
  if (!email) return null;

  const firstName = agent?.firstName || "";
  const lastName = agent?.lastName || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const isoAgentId = agent?.isoAgentId || "";

  return {
    key: email,
    label: [isoAgentId, fullName, email].filter(Boolean).join(" - "),
    value: email,
    id: isoAgentId,
    email,
  };
};

const AgentAsyncAutocomplete = ({
  t,
  formProps,
  defaultAgent,
  disabled,
  readonly,
  label,
  placeholder,
  errorMessage,
  redirectToAddAgentPage,
}) => {
  const { clearErrors, register, setValue, formState } = formProps;

  useEffect(() => {
    register("isoAgentEmail", { required: true });
    register("isoAgentId");
  }, [register]);

  const defaultAgentId = `${defaultAgent?.isoAgentId || ""}`.trim();
  const defaultAgentEmail = defaultAgent?.email || "";
  const defaultAgentKeyword = `${defaultAgentId || defaultAgentEmail}`.trim();
  const handleAgentSelect = useCallback(
    (nextAgent, { isUserChange }) => {
      setValue("isoAgentEmail", nextAgent?.value || "", {
        shouldValidate: true,
        shouldDirty: isUserChange,
      });
      setValue("isoAgentId", nextAgent?.id || null, {
        shouldValidate: true,
        shouldDirty: isUserChange,
      });
      if (nextAgent) clearErrors(["isoAgentEmail", "isoAgentId"]);
    },
    [clearErrors, setValue],
  );
  const {
    fetchOptions,
    handleChange,
    options,
    selectedOption,
    cacheKey,
    invalidateCache
  } = useAsyncOptionField({
    queryOptions: queryAgentOptions,
    mapOption: buildAgentOption,
    getDefaultKeyword: useCallback(
      () => defaultAgentKeyword,
      [defaultAgentKeyword],
    ),
    isDefaultOption: useCallback(
      (option) =>
        `${option.id}` === defaultAgentId || option.value === defaultAgentEmail,
      [defaultAgentEmail, defaultAgentId],
    ),
    onSelect: handleAgentSelect,
  });

  const handleAddAgentClick = useCallback(async () => {
    // Reset form internal value
    setValue("isoAgentEmail", "", {
      shouldDirty: false,
    });
    setValue("isoAgentId", null, {
      shouldDirty: false,
    });

    // Reset the cache to ensure the newly added agent appears in the autocomplete options
    invalidateCache();
    redirectToAddAgentPage();
  }, [invalidateCache, redirectToAddAgentPage]);

  return (
    <Stack
      direction="column"
      flexGrow={1}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={formBuilderParams.formItemRowGap}
      >
        <AsyncAutocomplete
          isCacheOptions={cacheKey}
          label={label}
          mandatory
          fetchOptions={fetchOptions}
          preloadedOptions={options}
          value={selectedOption}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled || readonly}
          error={!!formState.errors?.isoAgentEmail}
          errorMessage={errorMessage}
          sx={{
            flexBasis: 600
          }}
        />
        {!readonly &&
          <ButtonMAS
            variant="primary"
            onClick={handleAddAgentClick}
            label="Add Agent"
          />
        }
      </Stack>
      <Typography variant="content">
        {t(`${COMPONENT_LOCALE_KEY}agentAssignment.helperText`)}
      </Typography>
    </Stack>
  );
};

export default AgentAsyncAutocomplete;
